import React, { useEffect, useState } from 'react'
import ReactEchartsCore from 'echarts-for-react/lib/core'
import echarts from 'echarts/lib/echarts'
import 'echarts/lib/chart/tree'
import 'echarts/lib/chart/line'
import 'echarts/lib/chart/lines'
import 'echarts/lib/chart/bar'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/dataZoom'
import 'echarts/lib/component/brush'
import 'echarts/lib/component/toolbox'
import 'echarts/lib/component/title'
import 'echarts/lib/component/legend'
import 'echarts/lib/component/graphic'
import { IGraphProps } from '../utils'
import { useTheme } from '@mui/material'
import moment from 'moment'
import { DEV_COLOR, PROD_COLOR, TEST_COLOR } from '../../../assets/jss/material-dashboard-react'
import { formatNumber } from '../../../utils/utils'

const Graph = (props: IGraphProps) => {
  const { name, yAxisTitle, seriesData, loading, frequency, timeRange } = props
  const theme = useTheme()

  // Series data and no data states
  const [series, setSeries] = useState<any>()
  const [noData, setNoData] = useState(false)

  useEffect(() => {
    if (Array.isArray(seriesData)) {
      if (seriesData.length == 0) {
        setNoData(true)
        setSeries({
          name: name,
          data: [],
          type: 'line',
        })
      } else {
        setNoData(false)
        setSeries([
          {
            name: name,
            data: seriesData,
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: {
              color: theme.palette.mode === 'dark' ? '#a3a3a3' : '#525252',
              width: 3,
            },
            itemStyle: {
              color: theme.palette.mode === 'dark' ? '#a3a3a3' : '#525252',
              borderWidth: 2,
              borderColor: theme.palette.mode === 'dark' ? '#171717' : '#fff',
            },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  {
                    offset: 0,
                    color: theme.palette.mode === 'dark' ? 'rgba(163, 163, 163, 0.3)' : 'rgba(82, 82, 82, 0.2)',
                  },
                  {
                    offset: 1,
                    color: theme.palette.mode === 'dark' ? 'rgba(163, 163, 163, 0.05)' : 'rgba(82, 82, 82, 0.02)',
                  },
                ],
              },
            },
          },
        ])
      }
    } else {
      let data = [] as any
      if (Object.keys(seriesData).length == 0) {
        setNoData(true)
        setSeries({
          name: name,
          data: [],
          type: 'line',
          symbol: 'circle',
          lineStyle: {
            width: 2.5,
          },
        })
      } else {
        setNoData(false)
        Object.keys(seriesData).forEach((key) => {
          const baseColor =
            key == 'dev' ? DEV_COLOR : key == 'test' ? TEST_COLOR : key == 'prod' ? PROD_COLOR : '#a3a3a3'
          data.push({
            name: key,
            data: seriesData[key],
            type: 'line',
            smooth: true,
            showSymbol: false,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: {
              width: 3,
              color: baseColor,
            },
            itemStyle: {
              color: baseColor,
              borderWidth: 2,
              borderColor: theme.palette.mode === 'dark' ? '#171717' : '#fff',
            },
            emphasis: {
              focus: 'series',
              lineStyle: {
                width: 4,
              },
            },
          })
        })
      }
      setSeries(data)
    }
  }, [seriesData])

  return (
    <ReactEchartsCore
      style={{ height: '100%' }}
      echarts={echarts}
      notMerge={true}
      showLoading={loading}
      loadingOption={{
        maskColor: theme.palette.mode == 'dark' ? 'rgb(0,0,0,0.8)' : 'rgb(255,255,255,0.8)',
        textColor: theme.palette.mode == 'dark' ? 'white' : 'black',
      }}
      option={{
        color: [DEV_COLOR, TEST_COLOR, PROD_COLOR],
        grid: { top: 40, bottom: 0, right: 40, left: 50, height: '83%', containLabel: true },
        graphic: {
          type: 'text',
          left: 'center',
          top: 'middle',
          invisible: !noData,
          style: {
            text: 'No data available',
            fill: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.5)',
            fontSize: 14,
            fontWeight: 500,
          },
        },
        tooltip: {
          show: true,
          trigger: 'axis',
          triggerOn: 'mousemove',
          confine: true,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(23, 23, 23, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(115, 115, 115, 0.3)' : 'rgba(0, 0, 0, 0.1)',
          borderWidth: 1,
          textStyle: {
            color: theme.palette.mode === 'dark' ? '#fff' : '#000',
            fontSize: 12,
          },
          padding: 12,
          formatter: (params) => {
            if (!params || !Array.isArray(params) || params.length === 0) return ''
            let date = moment(params[0].data[0]).format('MM/DD HH:mm')
            let tooltipContent = `${date}<br />`

            // Build tooltip dynamically based on available series (prod, test, dev order)
            ;[...params].reverse().forEach((param) => {
              let value = param.data[1]
              if (value == null) {
                value = '-'
              } else if (typeof value === 'number') {
                value = formatNumber(value.toFixed(2))
              }
              const label = param.seriesName.charAt(0).toUpperCase() + param.seriesName.slice(1)
              tooltipContent += `${param.marker}${label}: ${value}<br />`
            })

            return tooltipContent
          },
        },
        xAxis: {
          type: 'time',
          name: 'Date',
          nameLocation: 'middle',
          nameGap: 25,
          nameTextStyle: {
            color: theme.palette.mode == 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            fontSize: 12,
            fontWeight: 500,
          },
          splitLine: {
            show: false,
          },
          axisLabel: {
            color: theme.palette.mode == 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            fontSize: 11,
            fontWeight: 500,
            showMinLabel: false,
            showMaxLabel: false,
            formatter: function (value: string) {
              if (frequency == 'hourly' && timeRange == 1) {
                return moment(value).format('HH:mm')
              } else {
                return moment.utc(value).format('MM/DD HH:mm')
              }
            },
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: theme.palette.mode == 'dark' ? 'rgba(64, 64, 64, 0.4)' : 'rgba(0, 0, 0, 0.1)',
              width: 1,
            },
          },
          axisTick: {
            show: false,
          },
        },
        yAxis: {
          name: yAxisTitle,
          nameLocation: 'middle',
          nameGap: 50,
          nameTextStyle: {
            color: theme.palette.mode == 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            fontSize: 12,
            fontWeight: 500,
          },
          type: 'value',
          splitLine: {
            show: true,
            lineStyle: {
              color: theme.palette.mode == 'dark' ? 'rgba(64, 64, 64, 0.2)' : 'rgba(0, 0, 0, 0.06)',
              type: 'dashed',
            },
          },
          axisLabel: {
            color: theme.palette.mode == 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            fontSize: 11,
            fontWeight: 500,
          },
          axisLine: {
            show: true,
          },
          axisTick: {
            show: false,
          },
        },
        series: series,
      }}
    ></ReactEchartsCore>
  )
}

export default Graph
